import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../lib/api";
import { ScoreGauge } from "../../components/ScoreGauge";
import { CompetencyRadar } from "../../components/CompetencyRadar";
import { scoreColor, levelMeta } from "../../lib/scoring";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { ArrowLeft, QrCode, TrendingUp, Target, GraduationCap, Loader2, Mail, Sparkles } from "lucide-react";

const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`} {...rest}>{children}</div>
);

export default function StudentProgression() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get(`/college/student/${id}/progression`).then((r) => setD(r.data)).catch(() => navigate("/college"));
  }, [id, navigate]);

  if (!d) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const s = d.student;
  const intel = d.intelligence;
  const p = intel.profile;
  const latestVid = d.history.length ? d.history[d.history.length - 1].verification_id : null;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/college/students")} data-testid="back-to-students" className="inline-flex items-center gap-2 text-sm font-bold text-ink-soft hover:text-ink"><ArrowLeft className="h-4 w-4" /> Back to Students</button>

      {/* Student header */}
      <Card className="p-6 flex flex-wrap items-center gap-6" style={d.branding?.primary_color ? { borderTopWidth: 3, borderTopColor: d.branding.primary_color } : undefined}>
        <ScoreGauge value={p.readiness} size={150} />
        <div className="flex-1 min-w-[200px]">
          <div className="text-xs font-bold uppercase tracking-wider text-brand mb-1">{s.student_code}</div>
          <h1 className="font-heading text-2xl font-bold text-ink">{s.name}</h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-soft mt-1">
            <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> {s.email}</span>
            <span>{s.branch}</span><span className="num">Batch {s.graduation_year}</span>
            <span className="flex items-center gap-1"><Target className="h-3.5 w-3.5" /> {s.target_role}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: levelMeta(p.readiness).bg, color: levelMeta(p.readiness).text }}>{p.level}</span>
            <span className="text-xs font-semibold text-emerald-600">{p.trend} · {p.interview_count} interviews</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 ml-auto">
          {(d.branding?.logo || s.college_name) && (
            <div className="flex items-center gap-2" data-testid="progression-college-brand">
              {d.branding?.logo && <img src={d.branding.logo} alt="College logo" data-testid="progression-logo" className="h-8 w-8 object-contain rounded-md" />}
              {s.college_name && <span className="text-sm font-bold text-ink">{s.college_name}</span>}
            </div>
          )}
          {latestVid && <Link to={`/scorecard/${latestVid}`} target="_blank" className="inline-flex items-center gap-2 text-sm font-bold text-white bg-slate-900 hover:bg-slate-800 px-4 py-2.5 rounded-full transition-colors"><QrCode className="h-4 w-4" /> QR Credential</Link>}
        </div>
      </Card>

      {!intel.has_data ? (
        <Card className="p-10 text-center text-ink-soft">This student has not completed any interviews yet.</Card>
      ) : (
        <>
          {/* Next action */}
          <Card className="p-6 bg-slate-900 text-white border-slate-900">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-blue-300 mb-2"><Sparkles className="h-4 w-4" /> Recommended Intervention</div>
            <div className="font-heading text-xl font-bold">{intel.next_action.title}</div>
            <p className="text-slate-300 text-sm mt-1">{intel.next_action.detail}</p>
          </Card>

          {/* Progression + radar */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2 p-6">
              <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" /> Score Progression</div>
              {d.history.length > 1 ? (
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart data={d.history} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="n" tickFormatter={(n) => `#${n}`} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="overall" stroke="#0055FF" strokeWidth={3} dot={{ r: 5, fill: "#0055FF", strokeWidth: 2, stroke: "#fff" }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <div className="text-sm text-ink-soft py-10 text-center">Only one interview so far — trend appears after the next one.</div>}
            </Card>
            <Card className="p-6">
              <div className="font-heading font-bold text-ink mb-2">Skill Radar</div>
              {d.history.length ? <CompetencyRadar competencies={intel.gaps.reduce((a, g) => ({ ...a, [g.skill]: g.current }), {})} height={240} /> : null}
            </Card>
          </div>

          {/* Skill gaps + improvement + courses */}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="font-heading font-bold text-ink mb-4">Skill Gaps</div>
              <div className="space-y-3">
                {intel.gaps.map((g) => (
                  <div key={g.skill}>
                    <div className="flex justify-between text-sm mb-1"><span className="font-medium text-ink">{g.label}</span><span className="num text-xs text-ink-faint">{g.current} → {g.target}</span></div>
                    <div className="relative h-2 rounded-full bg-slate-100"><div className="absolute h-full rounded-full" style={{ width: `${g.current}%`, background: scoreColor(g.current) }} /><div className="absolute top-1/2 -translate-y-1/2 h-3 w-0.5 bg-slate-900" style={{ left: `${g.target}%` }} /></div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-6">
              <div className="font-heading font-bold text-ink mb-4">Improvement Plan</div>
              <div className="space-y-2.5">
                {intel.roadmap.weeks.map((w, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex justify-between"><span className="text-xs font-bold text-brand">{w.week}</span><span className="text-[10px] num text-ink-faint">Day {w.days}</span></div>
                    <div className="text-sm font-semibold text-ink">{w.focus}</div>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-6">
              <div className="font-heading font-bold text-ink mb-4 flex items-center gap-2"><GraduationCap className="h-4 w-4 text-brand" /> Courses</div>
              <div className="space-y-2.5">
                {intel.courses.slice(0, 4).map((c) => (
                  <div key={c.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="text-sm font-semibold text-ink leading-tight">{c.title}</div>
                    <div className="text-xs text-ink-faint mt-0.5">{c.provider} · For {c.skill_label}</div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Interview history */}
          <Card className="p-6">
            <div className="font-heading font-bold text-ink mb-4">Interview History</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200">
                  <th className="px-3 py-2.5">#</th><th className="px-3 py-2.5">Date</th><th className="px-3 py-2.5">Type</th><th className="px-3 py-2.5">Score</th><th className="px-3 py-2.5 text-right">Scorecard</th></tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {d.history.map((h) => (
                    <tr key={h.n}>
                      <td className="px-3 py-3 num text-ink-faint">{h.n}</td>
                      <td className="px-3 py-3 num text-ink-soft">{h.date}</td>
                      <td className="px-3 py-3 text-ink-soft">{h.type === "ai" ? "AI" : "Expert"}</td>
                      <td className="px-3 py-3"><span className="num font-bold" style={{ color: scoreColor(h.overall) }}>{h.overall}</span></td>
                      <td className="px-3 py-3 text-right"><Link to={`/scorecard/${h.verification_id}`} target="_blank" className="text-brand inline-flex"><QrCode className="h-4 w-4" /></Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
